import {
	VatCorrection,
	SetVatCorrectionRequest,
	VatsApi,
	Vat,
	VatVolumeReference,
	SetVatVolumeReferenceRequest,
} from '@/api';
import { ApiUtils } from '@/common/ApiUtils';
import {
	AdditionalVatParams,
	CustomFormulaCalculator,
	CustomFormulaResult,
	Millimeters,
} from '@/typings/custom-formula-calculator';

export class VatCorrectionService {
	private vatApi: VatsApi = new VatsApi();

	public static async calculateMilkVatVolume(
		distances: Millimeters[],
		additionalVatParams: AdditionalVatParams,
		vat: Vat | null,
		parameters: Record<string, string | number>,
		correction: Record<string, number | null>,
		reading?: number,
	): Promise<{ result?: CustomFormulaResult | Error; error?: string }> {
		if (vat?.id == undefined || !vat?.formula) return { error: 'ID or Formula not specified' };

		try {
			const result: CustomFormulaResult[] | Error = CustomFormulaCalculator.calculateVolume({
				distances,
				entityType: 'vat',
				entityId: vat.id,
				entityParameters: parameters,
				entityCorrections: correction,
				formula: (<any>vat.customformula)?.formula,
				formulaName: (<any>vat.formula)?.name,
				additionalVatParams,
			});
			if (result instanceof Error) {
				return { error: result.message };
			}
			return {
				result: result[0],
			};
		} catch (ex) {
			const error = ApiUtils.handleApiError(ex, 'Unknown reason').errorMessage;
			return { error: 'Error calculating milk vat volume: ' + error };
		}
	}

	public async getCorrections(
		vatId: number,
		options?: any,
	): Promise<{ errorMessage?: string; corrections?: VatCorrection[] }> {
		try {
			const response = await this.vatApi.getVatCorrections(vatId, options);
			return { corrections: response.data };
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem getting the vat corrections');
		}
	}

	public async setCorrection(
		vatId: number,
		body: SetVatCorrectionRequest,
		options?: any,
	): Promise<{ errorMessage?: string }> {
		try {
			await this.vatApi.setVatCorrection(vatId, body, options);
			return {};
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem setting the vat correction');
		}
	}

	public async addReference(
		vatId: number,
		body: SetVatVolumeReferenceRequest,
		options?: any,
	): Promise<{ errorMessage?: string } | void> {
		try {
			await this.vatApi.setVatVolumeReference(vatId, body, options);
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem setting the vat reference point');
		}
	}

	public async getReferences(
		vatId: number,
		options?: any,
	): Promise<{ errorMessage?: string; references?: VatVolumeReference[] }> {
		try {
			const response = await this.vatApi.getVatVolumeReferences(vatId, options);
			return { references: response.data };
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem getting the vat reference points');
		}
	}

	public async deleteReference(
		vatId: number,
		referenceId: number,
		options?: any,
	): Promise<{ errorMessage?: string } | void> {
		try {
			const response = await this.vatApi.deleteVatVolumeReference(vatId, referenceId, options);
		} catch (e) {
			return ApiUtils.handleApiError(e, 'There was a problem deleting the vat reference point');
		}
	}
}
